/**
 * The receiver side of a TV session, independent of which Firebase app and
 * receiver name it runs with (split out of tvSession.ts, 2026-09-26). The web
 * page binds it to its own Firestore and Functions in tvSession.ts; the Apple
 * TV app (web/tv/appletv/) binds it to its React Native Firebase setup and the
 * name "Apple TV". Behavior is unchanged.
 */
import { doc, onSnapshot, type Firestore, type Unsubscribe } from 'firebase/firestore';
import { httpsCallable, type Functions } from 'firebase/functions';
import {
  type TvReceiverCommand,
  type TvRequestedCommand,
  TV_GAME_SCHEMA_CAPABILITY,
  TV_PROTOCOL_VERSION,
  type CreatedTvSession,
  type ResumedTvSession,
  type TvSessionDocument,
} from './protocol';
import type { StoredTvDevice } from './tvDevice';
import { unwrapNestedArrays } from './nestedArrays';
import { tvLatencyRecorder, type TvLatencyReport } from '../../../packages/putt-scene/src/tvLatency';

/** What the web receiver can render; the phone reads it from the session doc. */
export const RECEIVER_CAPABILITIES = [TV_GAME_SCHEMA_CAPABILITY];
export const HEARTBEAT_INTERVAL_MS = 45_000;
/** One early heartbeat after the first putt, so the TV learns its clock
 *  offset (and reports) without waiting out the 45 s interval. */
export const EARLY_HEARTBEAT_DELAY_MS = 3_000;

export interface TvReceiverConnection {
  sessionId: string;
  expiresAtMs: number;
  /** Set on a fresh (unpaired) session; null when resumed from a device token. */
  pairing: { code: string; expiresAtMs: number } | null;
  deviceId: string | null;
  subscribe: (
    listener: (session: TvSessionDocument) => void,
    onError: (error: Error) => void,
  ) => Unsubscribe;
  /** `reason` is recorded on the session for diagnosis (which path ended it). */
  close: (reason?: string) => Promise<void>;
  /** Send the phone a command with this session's own receiver secret (Sim 9
   *  chase RESTART, chaseRestart.ts). */
  sendCommand: (command: TvRequestedCommand) => Promise<TvReceiverCommand>;
}

export interface TvReceiverClientOptions {
  db: Firestore;
  functions: Functions;
  /** Sent to createTvSession; the account can rename the TV later. */
  receiverName: string;
  /** A list, or a function read at each create and resume (the Apple TV
   *  adds the sound capability only once its audio is ready). */
  receiverCapabilities?: readonly string[] | (() => readonly string[]);
  /** "appletv" or "web" on the putt latency reports (packages/putt-scene/src/tvLatency.ts). */
  receiverKind?: string;
  /** Called after each heartbeat (ok or the error); diagnostics only. */
  onHeartbeat?: (result: { ok: true; status: string } | { ok: false; error: unknown }) => void;
}

export interface TvReceiverClient {
  createReceiver(): Promise<TvReceiverConnection>;
  resumeReceiver(device: StoredTvDevice): Promise<TvReceiverConnection>;
  sendReceiverCommand(
    target: { sessionId: string; receiverSecret: string },
    command: TvRequestedCommand,
  ): Promise<TvReceiverCommand>;
}

export function createTvReceiverClient(options: TvReceiverClientOptions): TvReceiverClient {
  const { db, functions, receiverName } = options;
  const capabilitiesOption = options.receiverCapabilities ?? RECEIVER_CAPABILITIES;
  const receiverCapabilities = (): string[] => [
    ...(typeof capabilitiesOption === 'function' ? capabilitiesOption() : capabilitiesOption),
  ];
  const createSession = httpsCallable<
    { receiverName: string; receiverCapabilities: string[] },
    CreatedTvSession
  >(functions, 'createTvSession');
  const resumeSession = httpsCallable<
    { deviceToken: string; receiverName: string; receiverCapabilities: string[] },
    ResumedTvSession
  >(functions, 'resumeTvSession');
  const heartbeat = httpsCallable<
    { sessionId: string; receiverSecret: string; latency?: TvLatencyReport[] },
    { ok: boolean; status: string; serverNowMs?: number }
  >(functions, 'heartbeatTvSession');
  tvLatencyRecorder.setReceiver(options.receiverKind ?? 'web');
  const closeSession = httpsCallable<{ sessionId: string; secret: string; reason?: string }, { ok: boolean }>(
    functions,
    'closeTvSession',
  );
  const sendCommand = httpsCallable<
    { sessionId: string; receiverSecret: string; command: TvRequestedCommand },
    { ok: true; command: TvReceiverCommand }
  >(functions, 'sendTvCommand');

  function connection(input: {
    sessionId: string;
    receiverSecret: string;
    expiresAtMs: number;
    pairing: TvReceiverConnection['pairing'];
    deviceId: string | null;
  }): TvReceiverConnection {
    let stopped = false;
    // Putt latency rides on the heartbeat: the reports go to the server (an
    // older server ignores the field) and the reply's server time is the
    // TV's clock-offset sample. No extra writes, no extra callables except
    // the single early heartbeat below.
    const beat = () => {
      if (stopped) return;
      const latency = tvLatencyRecorder.pendingReports();
      const sentMs = Date.now();
      void heartbeat({
        sessionId: input.sessionId,
        receiverSecret: input.receiverSecret,
        ...(latency.length ? { latency } : {}),
      }).then(
        (result) => {
          tvLatencyRecorder.clock.sample(sentMs, result.data?.serverNowMs, Date.now());
          tvLatencyRecorder.markReported(latency.map((report) => report.id));
          options.onHeartbeat?.({ ok: true, status: String(result.data?.status ?? '') });
        },
        (error) => options.onHeartbeat?.({ ok: false, error }),
      );
    };
    const heartbeatTimer = setInterval(beat, HEARTBEAT_INTERVAL_MS);
    let earlyBeatTimer: ReturnType<typeof setTimeout> | null = null;
    let earlyBeatSent = false;
    const unwatchLatency = tvLatencyRecorder.subscribe(() => {
      if (stopped || earlyBeatSent || earlyBeatTimer || !tvLatencyRecorder.wantsEarlyHeartbeat()) return;
      earlyBeatTimer = setTimeout(() => {
        earlyBeatTimer = null;
        earlyBeatSent = true;
        beat();
      }, EARLY_HEARTBEAT_DELAY_MS);
    });

    return {
      sendCommand: async (command) => {
        const result = await sendCommand({ sessionId: input.sessionId, receiverSecret: input.receiverSecret, command });
        return result.data.command;
      },
      sessionId: input.sessionId,
      expiresAtMs: input.expiresAtMs,
      pairing: input.pairing,
      deviceId: input.deviceId,
      subscribe(listener, onError) {
        return onSnapshot(
          doc(db, 'tv_sessions', input.sessionId),
          (snapshot) => {
            if (!snapshot.exists()) return onError(new Error('TV session no longer exists.'));
            const data = snapshot.data();
            // Arrival time first, before any parsing or rendering work.
            tvLatencyRecorder.noteSessionDoc(data, { fromCache: snapshot.metadata.fromCache });
            listener(unwrapNestedArrays(data) as TvSessionDocument);
          },
          onError,
        );
      },
      async close(reason?: string) {
        if (stopped) return;
        stopped = true;
        clearInterval(heartbeatTimer);
        if (earlyBeatTimer) clearTimeout(earlyBeatTimer);
        unwatchLatency();
        await closeSession({ sessionId: input.sessionId, secret: input.receiverSecret, reason }).catch(
          () => undefined,
        );
      },
    };
  }

  return {
    /** A TV with no stored token: open a session that shows a pairing code. */
    async createReceiver() {
      const result = await createSession({ receiverName, receiverCapabilities: receiverCapabilities() });
      const created = result.data;
      if (created.protocolVersion !== TV_PROTOCOL_VERSION) throw new Error('This TV page needs an update.');
      return connection({
        sessionId: created.sessionId,
        receiverSecret: created.receiverSecret,
        expiresAtMs: created.expiresAtMs,
        pairing: { code: created.pairingCode, expiresAtMs: created.pairingExpiresAtMs },
        deviceId: null,
      });
    },
    /** A TV the account already knows: open a session bound to its device. */
    async resumeReceiver(device) {
      const result = await resumeSession({
        deviceToken: device.token,
        receiverName: device.name || receiverName,
        receiverCapabilities: receiverCapabilities(),
      });
      const resumed = result.data;
      if (resumed.protocolVersion != null && resumed.protocolVersion !== TV_PROTOCOL_VERSION) {
        throw new Error('This TV page needs an update.');
      }
      if (!resumed.sessionId || !resumed.receiverSecret) throw new Error('The TV session could not be resumed.');
      return connection({
        sessionId: resumed.sessionId,
        receiverSecret: resumed.receiverSecret,
        expiresAtMs: Number(resumed.expiresAtMs) || Date.now() + 12 * 60 * 60_000,
        pairing: null,
        deviceId: resumed.deviceId ?? device.deviceId,
      });
    },
    /**
     * Kept for a remote-bearing TV app (TV_REVIVAL_PLAN.md §1.4). The web page
     * has no controls and never calls this.
     */
    async sendReceiverCommand(target, command) {
      const result = await sendCommand({ ...target, command });
      return result.data.command;
    },
  };
}

/** The Firebase callable error code ('not-found', 'unavailable', ...) or null. */
export function callableErrorCode(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? code : null;
}
